import { type ObjectDefinition, objects } from "@dust/world/internal";
import { Checkbox, TableWithColumns } from "@permutation-city/ui";
import { useCallback, useEffect } from "react";
import { useAppStore } from "../../store/appStore";
import { BlueprintErrorHandler } from "./components/BlueprintErrorHandler";
import { useBlueprintCommon } from "./hooks/useBlueprintCommon";
import { useBlueprintTableData } from "./hooks/useBlueprintTableData";

export type Vec3 = [number, number, number];

export const objectsByObjectType = Object.fromEntries(
  Object.entries(objects).map(([, value]) => [value.id, value]),
) as Record<number, ObjectDefinition>;

export function Blueprint() {
  const {
    activeObjectTypeId,
    setActiveObjectTypeId,
    setAutoSwitch,
    matchInventory,
    setMatchInventory,
  } = useAppStore();

  const {
    dustClient,
    blueprint,
    isBlueprintLoading,
    handleAddWaypoint,
    isTooFarFromCity,
    isExpandingForceField,
    forceFieldError,
    isEnemyTerritory,
  } = useBlueprintCommon();

  const { blueprintItemsTableData } = useBlueprintTableData(blueprint);

  const setBlueprint = useCallback(async () => {
    if (!dustClient || !blueprint) return;

    const filteredBlueprint =
      blueprint?.filter(({ objectTypeId }: { objectTypeId: number }) => {
        if (activeObjectTypeId > 1) {
          return activeObjectTypeId === objectTypeId;
        }

        return true;
      }) || [];

    await dustClient.provider.request({
      method: "setBlueprint",
      params: {
        blocks:
          filteredBlueprint.map(
            ({
              objectTypeId,
              position,
              orientation,
            }: {
              objectTypeId: number;
              position: Vec3;
              orientation?: number;
            }) => ({
              objectTypeId,
              x: position[0],
              y: position[1],
              z: position[2],
              orientation: orientation ?? 0,
            }),
          ) ?? [],
        options:
          activeObjectTypeId === 1
            ? {
                showBlocksToBuild: false,
                showBlocksToMine: true,
              }
            : undefined,
      },
    });
  }, [dustClient, blueprint, activeObjectTypeId]);

  useEffect(() => {
    setBlueprint();
  }, [setBlueprint]);

  return (
    <BlueprintErrorHandler
      isTooFarFromCity={isTooFarFromCity}
      isExpandingForceField={isExpandingForceField}
      forceFieldError={forceFieldError}
      isEnemyTerritory={isEnemyTerritory}
      onAddWaypoint={handleAddWaypoint}
    >
      <div className="space-y-4">
        <div>
          <Checkbox
            name="match-inventory"
            checked={matchInventory}
            onChange={() => setMatchInventory(!matchInventory)}
          >
            MATCH INVENTORY
          </Checkbox>
        </div>

        <TableWithColumns
          columns={[
            {
              name: "Name",
              key: "name",
            },
            {
              name: "Pesos",
              key: "reward",
            },
            {
              name: "Rep",
              key: "reputation",
            },
            {
              name: "Progress",
              key: "progress",
            },
          ]}
          activeRowId={activeObjectTypeId}
          onClick={(item) => {
            setActiveObjectTypeId(Number(item.id));
            setAutoSwitch(false);
          }}
          data={blueprintItemsTableData}
          loading={isBlueprintLoading}
        />
      </div>
    </BlueprintErrorHandler>
  );
}
